import type { DuplicateCandidate } from "../../../../types/api";
import { Button } from "../../../../components/Button";
import { summarizeBulkMerge } from "./duplicateSelection";

interface BulkMergeConfirmDialogProps {
  candidates: DuplicateCandidate[];
  /**
   * Set when the coach chose "everything": the count of groups in the
   * organization. There is no honest way to itemize thousands of groups, so the
   * dialog states the scale and what will happen instead of listing them.
   */
  allGroupCount?: number;
  isSubmitting: boolean;
  /**
   * Groups finished / total, while a run is in flight. A select-all run is sent
   * in batches, so the dialog counts them off rather than showing a bare spinner.
   */
  progress?: { done: number; total: number };
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Names the exact counts before an irreversible run. There is no unmerge, so the
 * dialog states that plainly rather than relying on the coach to know it.
 */
export const BulkMergeConfirmDialog = ({
  candidates,
  allGroupCount,
  isSubmitting,
  progress,
  onCancel,
  onConfirm,
}: BulkMergeConfirmDialogProps) => {
  const summary = summarizeBulkMerge(candidates);
  const isMergeAll = allGroupCount !== undefined;

  if (isMergeAll) {
    return (
      <div className="duplicates__overlay" role="presentation">
        <div
          className="duplicates__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-merge-title"
        >
          <h2 id="bulk-merge-title" className="duplicates__dialog-title">
            Merge all {allGroupCount.toLocaleString()} groups?
          </h2>

          <p className="duplicates__dialog-body">
            Every duplicate group in your organization will be merged into the oldest
            record in each one. Teams, jersey numbers, notes, videos, and tags move to
            the surviving record.
          </p>
          <p className="duplicates__dialog-body">
            This runs in the background and can take a while. You can keep working or
            close this page.
          </p>

          <p className="duplicates__dialog-danger">Merges cannot be undone.</p>

          <div className="duplicates__dialog-actions">
            <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={onConfirm} loading={isSubmitting}>
              Merge all {allGroupCount.toLocaleString()}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="duplicates__overlay" role="presentation">
      <div
        className="duplicates__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-merge-title"
      >
        <h2 id="bulk-merge-title" className="duplicates__dialog-title">
          Merge {summary.groupCount} {summary.groupCount === 1 ? "group" : "groups"}?
        </h2>

        <p className="duplicates__dialog-body">
          {summary.playersAffected}{" "}
          {summary.playersAffected === 1 ? "player record" : "player records"} will be
          merged into the oldest record in each group. Teams, jersey numbers, notes,
          videos, and tags move to the surviving record.
        </p>

        {summary.tagConflictCount > 0 && (
          <p className="duplicates__warning" role="status">
            {summary.tagConflictCount}{" "}
            {summary.tagConflictCount === 1 ? "group has" : "groups have"} conflicting
            custom tag values. The surviving record&apos;s value is kept and the others
            are dropped.
          </p>
        )}

        <p className="duplicates__dialog-danger">Merges cannot be undone.</p>

        {progress && progress.total > 0 && (
          <p className="duplicates__dialog-progress" role="status" data-testid="bulk-merge-progress">
            Merging {Math.min(progress.done, progress.total)} of {progress.total}…
          </p>
        )}

        <div className="duplicates__dialog-actions">
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onConfirm} loading={isSubmitting}>
            Merge {summary.groupCount}
          </Button>
        </div>
      </div>
    </div>
  );
};
